import template from './ageEstimation.tpl';
import BaseStep from './../BaseStep';
import {gsap} from 'gsap';
import LifespanModel from '../../model/lifespanModel';
import ContentModel from '../../../../model/contentModel'
import AppStateModel from '../../../../model/appStateModel';
import MainController from '../../../../controller/MainController';
import StepEvent from './../StepEvent';
import ViewportUtility from '@/core/utils/viewport/viewportUtil';
import EventDispatcher from '@/core/event/eventDispatcher';
import ViewportEvent from '@/core/utils/viewport/viewportEvents';
import Gauge from './../../components/gauge/gauge';
import Knob from './../../components/knob/knob';
import YearInput from './../../components/yearInput/input';
import nextBtnTemplate from "@/app/components/templates/nextButton/nextButton.tpl";
import nextBtnStyle from "@/app/components/templates/nextButton/nextButton.module.scss";

export default class AgeEstimation extends BaseStep {

    private common: any;
    private limits: any;
    private gauge: any;
    private knob: any;
    private yearInput: any;

    constructor(id, data) {
        super(id, data);

        EventDispatcher.addEventListener(ViewportEvent.BREAKPOINT_CHANGE, (event) => this.breakPointChange(event));

        this.common = ContentModel.instance.getCommonData();

        // base components and func on a given limit interval (ex: 0 - 120)
        this.limits = {
            max: this.common.limits.estimate.max_year,
            min: this.common.limits.estimate.min_year
        };
        if (this.limits.min > this.limits.max) {
            this.limits.max = this.limits.min + 1;
        }

        this._data.limits = this.limits;
        this._data.gender_style = AppStateModel.gender;
        this._data.user_birthday = AppStateModel.birthday;
        this._data.next_button = nextBtnTemplate({data: this._data, styles: nextBtnStyle, options: {
                "next_step": this._data.steps.age_estimation.next_step,
                "is_round": this.common.layout.rounded_buttons ?? false
            }})


        // console.log("birthday",  this._data.user_birthday, typeof this._data.user_birthday);

        // set min value depending on how old the user is RIGHT NOW.
       // let currentYear = parseInt(new Date().getFullYear());
        this.limits.min = parseInt(this._data.user_birthday); //Math.max(this.limits.min, currentYear - this._data.user_birthday);

        this._template = template(this._data);
    }


    finishStep(item) {

        // save age estimation
        let age = this.yearInput.inputValue;

        AppStateModel.estimated_age = age;

        let target = item.getAttributeNode('data-target').value;
        //let target = item.dataset.target;

        let data = {
            age: age,
            next_step: target,
            id: this._id
        };

        if (target) {
            EventDispatcher.dispatchEvent(new StepEvent(StepEvent.STEP_FINISHED, data));
        }
    }


    render() {
        super.render();

        this.gauge = new Gauge(this.limits.min, this.limits.max);
        this.gauge.appendTo(this._element);
        this.gauge.inputValue = this.limits.min;

        this.knob = new Knob(this.limits.min, this.limits.max);
        this.knob.setOnChangeCallback((value) => this.onKnobChange(value));

        this.yearInput = new YearInput(this.limits.min, this.limits.max, this._data.common.year_of_birth_aria_label);
        this.yearInput.setOnChangeCallback((value) => this.onInputChange(value));
        this.yearInput.appendTo(this._element?.querySelector('div#knob_inner'));

    }

    postRender() {
        // TweenMax gives us errors when appended to elements which have: "display: none"
        if (this._element) {
            this._element.style.display = 'initial';
            this.knob.appendTo(this._element);
            this._element.style.display = 'none';
        }

        this.setAnimationElements();

        // set this knob to the same angle as previous knob (which now have a different value).
        if (LifespanModel.knob_rotation) {
            this.onKnobChange(this.knob.getValueFromRotation(LifespanModel.knob_rotation));
        }

        this.gauge.getElement().addEventListener('click', (event) => {
            this._onMouseDown(event)
        });

        // disable sharing for this view/step
        MainController.setSharing(false);
    }

    _onMouseDown(event) {

        let pos = {
            x: Math.round(event.clientX),
            y: Math.round(event.clientY)
        };
        this.knob.updateByMousePosition(pos.x, pos.y);
    }

    onInputChange(value) {

        if (this.knob) {
            this.knob.inputValue = value;
        }

        if (this.gauge) {
            this.gauge.inputValue = value;
        }
    }

    onKnobChange(value) {

        if (this.yearInput) {
            this.yearInput.inputValue = value;
        }

        if (this.gauge) {
            this.gauge.inputValue = value;
        }
    }

    setAnimationElements() {
        this.animationElements = {
            knob: this.knob.element,
            gauge: this.gauge.element,
            needle: this.gauge.element.querySelectorAll('.needle'),
            gender: this.element?.querySelector('#gender_icon'),
            input: this.yearInput.element,
            question: this.element?.querySelectorAll('.question'),
            legend: this.element?.querySelectorAll('.legend'),
            button: this.element?.querySelectorAll('.next_step_button'),
        }
    }

    destroy() {

        if (this.yearInput) {
            this.yearInput.destroy();
            this.yearInput = null;
        }

        if (this.gauge) {
            this.gauge.destroy();
            this.gauge = null;
        }

        if (this.knob) {
            this.knob.destroy();
            this.knob = null;
        }
        super.destroy();
    }

    getKnobScaleFromBreakpoint(breakpoint = '') {
        // Adaptation to get knob in correct scale for iframe.
        // ("transform: matrix(...)" is giving us trouble on animations by overwriting values)
        let scale;
        switch (breakpoint) {
            case 'w1024':
            case 'w880':
            case 'w750':
                scale = 'scale(1)';
                break;
            case 'w550':
                scale = 'scale(0.85)';
                break;
            default:
                scale = 'scale(0.6)';
                break;
        }
        return scale;
    }

    breakPointChange(event) {

        this.setElementScale(this.animationElements.knob, this.getKnobScaleFromBreakpoint(event.data));
    }

    setElementScale(element, scale) {

        element.style.transform = scale;
    }

    getHideAnimation() {
        let animations: any[] = [];

        let a = this.animationElements;
        let birthYearInner = [a.gender, a.input, a.question];

        // birth-year selection:
        animations.push(gsap.to(birthYearInner, 0.4, {autoAlpha: 0}));
        animations.push(gsap.to(a.knob, 0.5, {autoAlpha: 0}));
        animations.push(gsap.to(a.gauge, 1, {
            delay: 0.3,
            autoAlpha: 0,
            top: '-5%',
            transform: 'rotate(-65deg)'
        }));

        // next button:
        animations.push(gsap.to(a.button, 0.3, {autoAlpha: 0, display: 'none', bottom: '-10%'}));

        return animations;
    }

    getShowAnimation() {
        let animations: any[] = []; // gsap.Tween

        let a = this.animationElements;
        let ageEstimate = [a.input, a.question];
        let scale = this.getKnobScaleFromBreakpoint(ViewportUtility.breakpoint);

        // we animate the knob depending on it's previous position.
        //let average = this.randomizeKnobPosition(LifespanModel.getResultsByBirthdayAndGender().p);

        // note: IN THE END client wants to go back to lowest possible value, instead of random one above here:
        let average = this.limits.min;

        // deduce new relative angle | by subtracting the new absolute angle from the current angle (while adding a random padding to the value).
        let rotateFrom = this.knob.getRotationFromValue(this.yearInput.inputValue) - this.knob.getRotationFromValue(average);


        // birth-year selection:
        animations.push(gsap.from(ageEstimate, 0.5, {delay: 0.8, autoAlpha: 0}));
        animations.push(gsap.from(a.knob, 1.3, {transform: 'rotate(' + rotateFrom + 'deg) ' + scale}));
        animations.push(gsap.to(a.knob, 1.3, {transform: scale})); // workaround, since we have transform being overridden in css,
        animations.push(gsap.from(a.gauge, 1.3, {autoAlpha: 0}));

        // legend;
        animations.push(gsap.from(a.legend, 1, {autoAlpha: 0, 'margin-left': '-20em'}));

        // next button:
        animations.push(gsap.from(a.button, 0.3, {delay: 0.5, autoAlpha: 0, display: 'none', right: '-100px'}));

        // since we animated the knob position, trigger update for the destination value.
        this.onKnobChange(average);
        return animations;
    }

    randomizeKnobPosition(prognosisValue) {
        let average = prognosisValue;

        // but we also want to randomize the value, to avoid always giving a used "correct" estimate by default.
        let randomPadding = Math.random() * (this.common.limits.estimate.random_max - this.common.limits.estimate.random_min) + this.common.limits.estimate.random_min;
        // randomize the negative/positive part of the value
        randomPadding = Math.random() > 0.5 ? randomPadding : randomPadding - (randomPadding * 2);

        // add random and assure that it's within limits
        average = average + randomPadding;
        if (average > this.limits.max) average = this.limits.max;
        if (average < this.limits.min) average = this.limits.min;

        // return closest int.
        return Math.round(average);
    }
}
